import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ProductManagementState from "interfaces/state/ProductManagementState";
import {  
  getAllProductsAsync, 
  createProductAsync, 
  updateProductAsync, 
  deleteProductAsync } from "apis/ProductApi";
import ProductRequest from "interfaces/request/ProductRequest";
import AdminProduct from "interfaces/response/AdminProduct";
import { toastSuccess } from "components/common/ToastNotification";
import { RootState } from "store/store";
import { getBillingIntervalDropdownValues, getActionValidationDurationUnitDropdownValues } from "./DropdownValueSlice";

const initialState: ProductManagementState = {
  loading: false,
  selectedProduct: null,
  products: []
};

const updateProductInState = (
  ProductToBeUpdated: number,
  ProductDetails: AdminProduct,
  state: ProductManagementState
) => {
  const existingProductIndex = state.products.findIndex(
    (product: AdminProduct) => product.id === ProductToBeUpdated
  );
  if (existingProductIndex > -1) {
    state.products[existingProductIndex] = ProductDetails;
  }
};

const deleteProductFromState = (productId: number, state: ProductManagementState) => {
  const existingProductIndex = state.products.findIndex(
    (product: AdminProduct) => product.id === productId
  );
  if (existingProductIndex > -1) {
    state.products.splice(existingProductIndex, 1);
  }
}


export const loadLicenseRelatedDropdownData = createAsyncThunk<
  any,
  void,
  { state: RootState }
>("productManagement/loadLicenseRelatedDropdownData", async (_, thunkAPI) => {

  const billingIntervalDropdownValues = thunkAPI.dispatch(getBillingIntervalDropdownValues());
  const actionValidationDurationUnitDropdownValues = thunkAPI.dispatch(getActionValidationDurationUnitDropdownValues());

  return Promise.all([billingIntervalDropdownValues, actionValidationDurationUnitDropdownValues]);
});

export const getProductList = createAsyncThunk(
  "productManagement/getProductList",
  async () => {
    return await getAllProductsAsync();
  }
);

export const createNewProduct = createAsyncThunk(
  "productManagement/createNewProduct",
  async (postData: ProductRequest) => {
    return await createProductAsync(postData);
  }
);

export const updateProduct = createAsyncThunk<
  AdminProduct,
  { productId: number; productRequest: ProductRequest }
>(
  "productManagement/updateProduct", async (request) => {
  return await updateProductAsync(request.productId, request.productRequest);
});

export const deleteProduct = createAsyncThunk<any, {productId: number},  { state: RootState }>(
  "productManagement/deleteProduct", async (request) => {
    await deleteProductAsync(request.productId);
  }
);

export const productManagementSlice = createSlice({
  name: "productManagement",
  initialState,
  reducers: {
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProductList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getProductList.fulfilled, (state, action) => {
      state.loading = false;
      state.products = action.payload;
    });
    builder.addCase(getProductList.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createNewProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createNewProduct.fulfilled, (state, action) => {
      state.loading = false;
      state.products.unshift(action.payload);
    });
    builder.addCase(createNewProduct.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateProduct.fulfilled, (state, action) => {
      updateProductInState(action.payload.id, action.payload, state);
      toastSuccess("Product details updated.");
      state.loading = false;
    });
    builder.addCase(updateProduct.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteProduct.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteProduct.fulfilled, (state, action) => {
      deleteProductFromState(action.meta.arg.productId, state);
      toastSuccess("Product deleted.");
      state.loading = false;
    });
    builder.addCase(deleteProduct.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const { setSelectedProduct } = productManagementSlice.actions;
export default productManagementSlice.reducer;
